import { formatDate, formatDateTime, formatMoney, t } from "@hakili/i18n";
import { CheckCircle2, Clock, ExternalLink, MinusCircle, Sparkles, XCircle } from "lucide-react";
import { useState } from "react";

/**
 * `U-1`, `M3`: **« Lecture de Hakili » on the file** , the first pass's reading,
 * the chargée's two acts by her own doors, the trail.
 *
 * The block carries the drawing's name (`hakili-console.jsx`'s « Lecture de
 * Hakili », WF-CON-04 step 9); the drawing is silent on a field-by-field
 * reading, so its position , under the stage panel, in the file's main column,
 * where the chargée's acts sit , and its words are the register's, flagged in
 * the addendum. Everything shown is the door's (`GET /dossiers/{id}/first-pass`):
 * the completeness verdict per required piece, each field with its value, its
 * source piece and its box (the page and the coordinates named , no viewer
 * exists on main, R81), the contradictions in one sentence each, the proposed
 * quotité and the proposed pre-filter with each bank's reason, « Confirmer » and
 * « Corriger » per field, the count remaining, « Voir la lecture » opening the
 * trail, the failure in one sentence, and R81's sentence when no run stands.
 * Nothing here decides: the filter's gate is the door's, printed here in the
 * door's own words before the door says them.
 */
export interface FirstPassFieldView {
  readonly key: string;
  readonly documentCode: string;
  readonly label: string;
  readonly kind: "text" | "amount" | "date";
  readonly proposedValue: string | null;
  readonly sourceDocumentId: string | null;
  readonly box: {
    readonly page: number;
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  } | null;
  readonly confidencePct: number | null;
  readonly act: {
    readonly act: "confirmee" | "corrigee" | "manuelle";
    readonly value: string | null;
    readonly byName: string;
    readonly actedAt: string;
  } | null;
}

export interface FirstPassDocumentView {
  readonly code: string;
  readonly label: string;
  /** `VISITE-SANS-OBJET` (17 October 2026): `sansObjet` , satisfied, with the chargé's reason; the panel prints, never derives. */
  readonly state: "presente" | "absente" | "sansObjet";
  readonly reason: string;
  readonly reading: "lue" | "illisible" | "non_lue" | "echec" | null;
  readonly readingMessage: string | null;
  readonly refusal: string | null;
  readonly readingWhy: string | null;
}

export interface FirstPassStepView {
  readonly ordinal: number;
  readonly kind: string;
  readonly ruleName: string | null;
  readonly modelId: string | null;
  readonly promptId: string | null;
  readonly promptVersion: string | null;
  readonly startedAt: string;
  readonly endedAt: string;
  readonly failure: string | null;
}

/**
 * `P3`: what the dossier's own pièces read knows of a pièce , when it came, from whom, and whether the dealership
 * had it filled from the vehicle's papers. The run's stored sentence says the same in one string; the columns are
 * built from these, and the stored sentence stands where no pièce is handed.
 */
export interface ReadingDocumentFact {
  readonly code: string;
  readonly recuLe: string | null;
  readonly auteur: string | null;
  readonly provenance?: "vehicule" | "concessionnaire" | "emise" | null | undefined;
}

export interface FirstPassReadingView {
  readonly run: {
    readonly id: string;
    readonly status: "en_attente" | "en_cours" | "terminee";
    readonly submittedAt: string;
    readonly endedAt: string | null;
    readonly outcome: "reussie" | "en_echec" | null;
  };
  readonly completeness: { readonly missing: number; readonly complete: boolean } | null;
  readonly documents: readonly FirstPassDocumentView[];
  readonly fields: readonly FirstPassFieldView[];
  /**
   * `U-1` follow-up: one row per field of a kind Hakili never read , the
   * piece illegible, the reader unreachable for that kind alone, or never
   * filed. Optional: a fixture built before this round has none, and that
   * reads as « nothing to enter by hand », never as an absent door.
   */
  readonly manualFields?: readonly FirstPassFieldView[];
  readonly contradictions: readonly string[];
  readonly quotite: {
    readonly proposedPct: number | null;
    /**
     * `AA-3b` (one quotité): the ONE stored quotité (the filter's), or `null` while none stands. `proposedPct` is
     * only Hakili's proposal on the figures it read, before her acts; once this exists it is the quotité.
     */
    readonly recordedPct?: number | null;
    /**
     * `QUOTITE-PROFILE-STALE`: the door's own words when the stored quotité was withdrawn because the client edited his declared
     * commitments (« …confirmez à nouveau pour recalculer la quotité. »); `null` otherwise. Printed as given, nothing recomputed.
     */
    readonly withdrawnWords?: string | null;
    readonly declaredPct: number | null;
    readonly capacityAmount: number | null;
    readonly netSalaryAverageAmount: number | null;
  } | null;
  readonly prefilter: {
    readonly quotitePct: number;
    readonly retained: readonly { readonly bankId: string; readonly name: string; readonly reason: string }[];
    readonly excluded: readonly { readonly bankId: string; readonly name: string; readonly reason: string }[];
    readonly passed: boolean;
  } | null;
  readonly remaining: number;
  readonly failure: string | null;
  /** `G3-1(e)`: `reader_off` when no reader is configured, `failed` for a step that ran and failed. */
  readonly failureKind?: "reader_off" | "failed" | null;
  readonly steps: readonly FirstPassStepView[];
}

const fill = (key: string, values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));

/** The door's own sentence for the count remaining, mirrored here so the greyed control says it before the door does. */
export const remainingWords = (count: number): string =>
  count === 1
    ? t("console.lecture.restantUn")
    : fill("console.lecture.restantPlusieurs", { n: String(count) });

const readingWords = (piece: FirstPassDocumentView): string | null => {
  switch (piece.reading) {
    case "lue":
      return t("console.lecture.pieceLue");
    case "illisible":
      return t("console.lecture.pieceIllisible");
    case "non_lue":
      return t("console.lecture.pieceNonLue");
    case "echec":
      return t("console.lecture.pieceEchec");
    case null:
      return null;
  }
};

const stepWords = (step: FirstPassStepView): string => {
  const title =
    step.kind === "completeness"
      ? t("console.lecture.etapeCompleteness")
      : step.kind === "extraction"
        ? t("console.lecture.etapeExtraction")
        : step.kind === "arithmetic"
          ? t("console.lecture.etapeArithmetic")
          : t("console.lecture.etapePrefilter");
  const by =
    step.ruleName !== null
      ? fill("console.lecture.etapeRegle", { regle: step.ruleName })
      : fill("console.lecture.etapeModele", {
          modele: step.modelId ?? "",
          prompt: step.promptId ?? "",
          version: step.promptVersion ?? "",
        });
  return `${String(step.ordinal)}. ${title} · ${by} · ${formatDateTime(new Date(step.startedAt))} à ${formatDateTime(new Date(step.endedAt))}`;
};

const valueWords = (field: FirstPassFieldView, value: string | null): string => {
  if (value === null) return t("console.lecture.valeurNulle");
  if (field.kind === "amount" && /^\d+$/u.test(value)) return formatMoney(Number(value));
  if (field.kind === "date" && /^\d{4}-\d{2}-\d{2}$/u.test(value))
    return formatDate(new Date(`${value}T00:00:00.000Z`));
  return value;
};

function FieldRow(props: {
  readonly field: FirstPassFieldView;
  readonly documentLabel: string;
  readonly canAct: boolean;
  readonly busy: boolean;
  readonly onAct: (key: string, act: "confirmee" | "corrigee" | "manuelle", value: string | null) => void;
}): React.JSX.Element {
  const { field, documentLabel } = props;
  const [correcting, setCorrecting] = useState(false);
  const [draft, setDraft] = useState("");
  const source =
    field.box === null
      ? fill("console.lecture.sourceSansZone", { piece: documentLabel })
      : fill("console.lecture.source", {
          piece: documentLabel,
          page: String(field.box.page),
          x: String(field.box.x),
          y: String(field.box.y),
        });
  return (
    <li className="rounded-xl bg-gray-50 px-3.5 py-2.5" data-testid={`lecture-champ-${field.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-700">{field.label}</p>
          <p className="text-sm text-gray-900 tabular-nums" data-testid={`lecture-valeur-${field.key}`}>
            {valueWords(field, field.act?.value ?? field.proposedValue)}
            {field.confidencePct !== null ? (
              <span className="ml-2 text-sm text-gray-500">
                {fill("console.lecture.confiance", { pct: String(field.confidencePct) })}
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 text-sm text-gray-500">{source}</p>
          {field.act !== null ? (
            <p className="mt-1 text-sm text-emerald-700" data-testid={`lecture-acte-${field.key}`}>
              {field.act.act === "confirmee"
                ? fill("console.lecture.confirmeePar", { nom: field.act.byName })
                : fill("console.lecture.corrigeePar", {
                    nom: field.act.byName,
                    valeur: valueWords(field, field.act.value),
                    proposee: valueWords(field, field.proposedValue),
                  })}
            </p>
          ) : null}
        </div>
        {props.canAct ? (
          <div className="flex shrink-0 items-center gap-2">
            {field.act === null ? (
              <button
                type="button"
                data-testid={`lecture-confirmer-${field.key}`}
                disabled={props.busy}
                onClick={() => {
                  props.onAct(field.key, "confirmee", null);
                }}
                className="rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 px-3 py-1.5 text-xs font-medium disabled:opacity-40"
              >
                {t("console.lecture.confirmer")}
              </button>
            ) : null}
            <button
              type="button"
              data-testid={`lecture-corriger-${field.key}`}
              disabled={props.busy}
              onClick={() => {
                setDraft(field.act?.value ?? field.proposedValue ?? "");
                setCorrecting((now) => !now);
              }}
              className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              {t("console.lecture.corriger")}
            </button>
          </div>
        ) : null}
      </div>
      {correcting ? (
        <div
          className="mt-2 flex flex-wrap items-center gap-2"
          data-testid={`lecture-correction-${field.key}`}
        >
          <label className="text-sm text-gray-600">
            {t("console.lecture.valeurCorrigee")}
            <input
              data-testid={`lecture-correction-valeur-${field.key}`}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              className="ml-2 rounded-lg border border-gray-200 px-2 py-1 text-sm"
            />
          </label>
          <button
            type="button"
            data-testid={`lecture-correction-enregistrer-${field.key}`}
            disabled={props.busy || draft.trim() === ""}
            onClick={() => {
              props.onAct(field.key, "corrigee", draft.trim());
              setCorrecting(false);
            }}
            className="rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 px-3 py-1.5 text-xs font-medium disabled:opacity-40"
          >
            {t("console.lecture.enregistrerCorrection")}
          </button>
          <button
            type="button"
            onClick={() => {
              setCorrecting(false);
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {t("console.lecture.annuler")}
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * `U-1` follow-up, `P3`: **her third act, on the fields Hakili never produced , one card per pièce.**
 *
 * Deliberately NOT `FieldRow`: there is no proposal to show, no confidence, no box, and nothing to « confirmer ».
 * One « Enregistrer la saisie » for the pièce sends its typed fields in sequence through the one per-field door
 * (`onEnter`); a field the door refuses shows its own refusal in words and the others stay saved. A saved field
 * says who entered it and when, shows its value once, and offers « Modifier ` , never a live « Enregistrer ».
 */
function ManualDocumentCard(props: {
  readonly documentCode: string;
  readonly documentLabel: string;
  readonly fields: readonly FirstPassFieldView[];
  readonly canAct: boolean;
  readonly busy: boolean;
  readonly openable: boolean;
  readonly onEnter:
    | ((entries: readonly { key: string; value: string }[]) => Promise<Readonly<Record<string, string>>>)
    | undefined;
  readonly onOpenDocument: ((documentCode: string) => Promise<string | null>) | undefined;
}): React.JSX.Element {
  const [drafts, setDrafts] = useState<Readonly<Record<string, string>>>({});
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set());
  const [refusals, setRefusals] = useState<Readonly<Record<string, string>>>({});
  const [sending, setSending] = useState(false);
  const typed = props.fields.filter((one) => (drafts[one.key] ?? "").trim() !== "");
  const open = props.canAct && props.onEnter !== undefined;
  return (
    <li
      className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5"
      data-testid={`lecture-saisie-piece-${props.documentCode}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700">{props.documentLabel}</p>
        {props.openable && props.onOpenDocument !== undefined ? (
          <button
            type="button"
            data-testid={`lecture-ouvrir-piece-${props.documentCode}`}
            onClick={() => {
              void props.onOpenDocument?.(props.documentCode).then((url) => {
                if (url !== null) window.open(url, "_blank", "noopener");
              });
            }}
            className="flex items-center gap-1 text-sm font-medium text-indigo-700 hover:text-indigo-900"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            {t("console.lecture.ouvrirPiece")}
          </button>
        ) : null}
      </div>
      <ul className="mt-1.5 space-y-2">
        {props.fields.map((field) => {
          const saved = editing.has(field.key) ? null : field.act;
          return (
            <li key={field.key} data-testid={`lecture-saisie-${field.key}`}>
              <p className="text-sm text-gray-600">{field.label}</p>
              {saved !== null ? (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-gray-900 tabular-nums" data-testid={`lecture-acte-${field.key}`}>
                    {valueWords(field, saved.value)}
                  </p>
                  <p className="text-sm text-emerald-700">
                    {fill("console.lecture.saisiLe", {
                      nom: saved.byName,
                      quand: formatDateTime(new Date(saved.actedAt)),
                    })}
                  </p>
                  {open ? (
                    <button
                      type="button"
                      data-testid={`lecture-saisie-modifier-${field.key}`}
                      onClick={() => {
                        setDrafts((now) => ({ ...now, [field.key]: field.act?.value ?? "" }));
                        setEditing((now) => new Set([...now, field.key]));
                      }}
                      className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
                    >
                      {t("console.lecture.modifier")}
                    </button>
                  ) : null}
                </div>
              ) : open ? (
                <input
                  data-testid={`lecture-saisie-valeur-${field.key}`}
                  type={field.kind === "date" ? "date" : "text"}
                  inputMode={field.kind === "amount" ? "numeric" : undefined}
                  value={drafts[field.key] ?? ""}
                  onChange={(event) => {
                    setDrafts((now) => ({ ...now, [field.key]: event.target.value }));
                  }}
                  className="mt-0.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                />
              ) : null}
              {refusals[field.key] === undefined ? null : (
                <p
                  role="alert"
                  data-testid={`lecture-saisie-refus-${field.key}`}
                  className="mt-0.5 text-sm text-rose-600"
                >
                  {refusals[field.key]}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {open && props.fields.some((one) => one.act === null || editing.has(one.key)) ? (
        <button
          type="button"
          data-testid={`lecture-saisie-enregistrer-${props.documentCode}`}
          disabled={props.busy || sending || typed.length === 0}
          onClick={() => {
            const entries = typed.map((one) => ({ key: one.key, value: (drafts[one.key] ?? "").trim() }));
            setSending(true);
            setRefusals({});
            void props.onEnter?.(entries).then(
              (refused) => {
                setRefusals(refused);
                setEditing((now) => new Set([...now].filter((key) => refused[key] !== undefined)));
                setSending(false);
              },
              () => {
                setSending(false);
              },
            );
          }}
          className="mt-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {t("console.lecture.enregistrerSaisie")}
        </button>
      ) : null}
    </li>
  );
}

/** The pièces that carry a red sentence, grouped by it: four pièces refused alike say it once. */
function refusalGroups(documents: readonly FirstPassDocumentView[]): readonly {
  readonly message: string;
  readonly pieces: readonly string[];
  readonly whys: readonly string[];
}[] {
  const groups = new Map<string, { pieces: string[]; whys: string[] }>();
  for (const piece of documents) {
    if (piece.readingMessage === null) continue;
    const group = groups.get(piece.readingMessage) ?? { pieces: [], whys: [] };
    group.pieces.push(piece.label);
    const why =
      piece.readingWhy === null
        ? null
        : `${piece.refusal === null ? "" : `${piece.refusal} : `}${piece.readingWhy}`;
    if (why !== null && !group.whys.includes(why)) group.whys.push(why);
    groups.set(piece.readingMessage, group);
  }
  return [...groups.entries()].map(([message, group]) => ({ message, ...group }));
}

type Tone = "ok" | "echec" | "attente" | "neutre";
/** The tick follows the reading: a pièce Hakili could not read is never green. */
const toneOf = (piece: FirstPassDocumentView): Tone =>
  piece.state === "sansObjet"
    ? "neutre"
    : piece.state === "absente"
      ? "attente"
      : piece.reading === "illisible" || piece.reading === "echec"
        ? "echec"
        : "ok";

export function FirstPassSection(props: {
  readonly reading: FirstPassReadingView | null;
  /** The chargée may act: her file, an open stage. */
  readonly canAct: boolean;
  readonly busy: boolean;
  readonly refusal?: string | null | undefined;
  /** `P0-DOS-074`: the door's sentence for a field act refused once the file went to a bank, drawn as served. */
  readonly actRefusal?: string | null | undefined;
  readonly onAct: (key: string, act: "confirmee" | "corrigee" | "manuelle", value: string | null) => void;
  /** `P3`: the dossier's own pièces, for the columns; absent, the run's stored sentence stands. */
  readonly pieces?: readonly ReadingDocumentFact[] | undefined;
  /** `P3`: the typed fields of one pièce, sent in sequence by the per-field door; answers the refusals by field key. */
  readonly onEnter?:
    | ((entries: readonly { key: string; value: string }[]) => Promise<Readonly<Record<string, string>>>)
    | undefined;
  /** `P3`: the consult door , the pièce's address, opened in a new tab. */
  readonly onOpenDocument?: ((documentCode: string) => Promise<string | null>) | undefined;
}): React.JSX.Element {
  const { reading } = props;
  const [trailOpen, setTrailOpen] = useState(false);
  const labelOf = (code: string): string =>
    reading?.documents.find((one) => one.code === code)?.label ?? code;
  return (
    <section data-testid="lecture-hakili" className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-indigo-600" aria-hidden="true" />
        <h3 className="text-sm font-semibold">{t("console.lecture.titre")}</h3>
      </div>
      {props.refusal !== null && props.refusal !== undefined ? (
        <p role="alert" data-testid="lecture-refus" className="mb-2 text-sm text-rose-600">
          {props.refusal}
        </p>
      ) : null}
      {props.actRefusal !== null && props.actRefusal !== undefined ? (
        <div
          role="alert"
          data-testid="lecture-refus-figee"
          data-tone="warn"
          className="mb-2 rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700"
        >
          {props.actRefusal}
        </div>
      ) : null}
      {/* `G5-2`: the run this panel shows is DATED, above its result , a run from before the reader was configured
          must never read as current. Its stored words are not rewritten; the date says how old they are. A run not
          yet ended is dated by its request. */}
      {reading === null ? null : (
        <p className="mb-2 text-sm text-gray-500" data-testid="lecture-derniere">
          {reading.run.status === "terminee"
            ? fill("console.lecture.derniere", {
                quand: formatDateTime(new Date(reading.run.endedAt ?? reading.run.submittedAt)),
              })
            : fill("console.lecture.lancee", { quand: formatDateTime(new Date(reading.run.submittedAt)) })}
        </p>
      )}
      {reading === null ? (
        <p className="text-sm text-gray-500" data-testid="lecture-absente">
          {t("console.lecture.absente")}
        </p>
      ) : reading.run.status !== "terminee" ? (
        <p className="text-sm text-gray-500" data-testid="lecture-en-attente">
          {reading.run.status === "en_attente"
            ? t("console.lecture.enAttente")
            : t("console.lecture.enCours")}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {reading.run.outcome === "en_echec" ? (
            <p
              className="rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm leading-relaxed text-rose-700"
              data-testid="lecture-echec"
            >
              {reading.failureKind === "reader_off"
                ? t("console.lecture.nonActivee")
                : fill("console.lecture.echec", { motif: reading.failure ?? "" })}
            </p>
          ) : null}

          <div data-testid="lecture-completude">
            <p className="text-sm font-medium text-gray-700">{t("console.lecture.completudeTitre")}</p>
            {reading.completeness !== null ? (
              <p className="mt-0.5 text-sm text-gray-900" data-testid="lecture-completude-verdict">
                {reading.completeness.complete
                  ? t("console.lecture.complete")
                  : reading.completeness.missing === 1
                    ? t("console.lecture.incompleteUne")
                    : fill("console.lecture.incompletePlusieurs", {
                        n: String(reading.completeness.missing),
                      })}
              </p>
            ) : null}
            <ul className="mt-1.5 space-y-1">
              <li
                aria-hidden="true"
                className="grid grid-cols-[minmax(0,2fr)_6rem_8rem_minmax(0,1.5fr)_minmax(0,1.5fr)] gap-x-3 px-1 text-xs text-gray-500"
              >
                <span>{t("console.lecture.colPiece")}</span>
                <span>{t("console.lecture.colEtat")}</span>
                <span>{t("console.lecture.colRecue")}</span>
                <span>{t("console.lecture.colSource")}</span>
                <span>{t("console.lecture.colLecture")}</span>
              </li>
              {reading.documents.map((piece) => {
                const fact = props.pieces?.find((one) => one.code === piece.code);
                const tone = toneOf(piece);
                const stateWords =
                  piece.state === "presente"
                    ? t("console.lecture.piecePresente")
                    : piece.state === "sansObjet"
                      ? t("console.lecture.pieceSansObjet")
                      : t("console.lecture.pieceAbsente");
                return (
                  <li
                    key={piece.code}
                    className="grid grid-cols-[minmax(0,2fr)_6rem_8rem_minmax(0,1.5fr)_minmax(0,1.5fr)] items-start gap-x-3 px-1 text-sm"
                    data-testid={`lecture-piece-${piece.code}`}
                  >
                    <span className="flex items-start gap-2 text-gray-700">
                      <span data-testid={`lecture-piece-${piece.code}-pastille`} data-tone={tone}>
                        {tone === "ok" ? (
                          <CheckCircle2
                            className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                            aria-hidden="true"
                          />
                        ) : tone === "echec" ? (
                          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
                        ) : tone === "neutre" ? (
                          <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                        ) : (
                          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                        )}
                      </span>
                      {piece.label}
                    </span>
                    <span className="text-gray-700">{stateWords}</span>
                    {fact !== undefined && piece.state === "presente" ? (
                      <>
                        <span className="text-gray-700" data-testid={`lecture-piece-${piece.code}-recue`}>
                          {fact.recuLe === null ? "" : formatDate(new Date(fact.recuLe))}
                        </span>
                        <span className="text-gray-700" data-testid={`lecture-piece-${piece.code}-source`}>
                          {fact.provenance === "vehicule"
                            ? t("console.lecture.sourceVehicule")
                            : (fact.auteur ?? "")}
                        </span>
                      </>
                    ) : (
                      <span className="col-span-2 text-gray-700">{piece.reason}</span>
                    )}
                    <span className="text-gray-500">{readingWords(piece) ?? ""}</span>
                  </li>
                );
              })}
            </ul>
            {refusalGroups(reading.documents).map((group) => (
              <div key={group.message} className="mt-1.5" data-testid="lecture-refus-groupe">
                <p className="text-sm text-rose-600">{group.message}</p>
                <p className="text-sm text-gray-500">{group.pieces.join(", ")}</p>
                {group.whys.map((why) => (
                  <p key={why} className="text-sm text-gray-500">
                    {why}
                  </p>
                ))}
              </div>
            ))}
          </div>

          <div data-testid="lecture-champs">
            <p className="text-sm font-medium text-gray-700">{t("console.lecture.champsTitre")}</p>
            {reading.fields.length === 0 ? (
              <p className="mt-0.5 text-sm text-gray-500">{t("console.lecture.aucunChamp")}</p>
            ) : (
              <ul className="mt-1.5 space-y-1.5">
                {reading.fields.map((field) => (
                  <FieldRow
                    key={field.key}
                    field={field}
                    documentLabel={labelOf(field.documentCode)}
                    canAct={props.canAct}
                    busy={props.busy}
                    onAct={props.onAct}
                  />
                ))}
              </ul>
            )}
          </div>

          {reading.manualFields !== undefined && reading.manualFields.length > 0 ? (
            <div data-testid="lecture-saisie-manuelle">
              <p className="text-sm font-medium text-gray-700">{t("console.lecture.saisieManuelleTitre")}</p>
              <p className="mt-0.5 text-sm text-gray-500">{t("console.lecture.saisieManuelleAide")}</p>
              <ul className="mt-1.5 space-y-1.5">
                {[...new Set(reading.manualFields.map((one) => one.documentCode))].map((code) => (
                  <ManualDocumentCard
                    key={code}
                    documentCode={code}
                    documentLabel={labelOf(code)}
                    fields={(reading.manualFields ?? []).filter((one) => one.documentCode === code)}
                    canAct={props.canAct}
                    busy={props.busy}
                    openable={reading.documents.find((one) => one.code === code)?.state === "presente"}
                    onEnter={props.onEnter}
                    onOpenDocument={props.onOpenDocument}
                  />
                ))}
              </ul>
            </div>
          ) : null}

          <div data-testid="lecture-contradictions">
            <p className="text-sm font-medium text-gray-700">{t("console.lecture.contradictionsTitre")}</p>
            {reading.fields.length === 0 ? (
              // `S4-13(c)`: zero fields read is NOT the same fact as zero contradictions found , the
              // first is nothing compared, the second is a comparison that cleared. Conflating them
              // reads as a reassuring « tout va bien » exactly when nothing was checked.
              <p className="mt-0.5 text-sm text-gray-500">{t("console.lecture.aucuneLecture")}</p>
            ) : reading.contradictions.length === 0 ? (
              <p className="mt-0.5 text-sm text-gray-500">{t("console.lecture.aucuneContradiction")}</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {reading.contradictions.map((sentence) => (
                  <li key={sentence} className="flex items-start gap-2 text-sm text-rose-700">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{sentence}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {reading.quotite !== null ? (
            <div data-testid="lecture-quotite" className="text-sm text-gray-900">
              {reading.quotite.recordedPct !== null && reading.quotite.recordedPct !== undefined ? (
                <p data-testid="lecture-quotite-retenue">
                  {fill("console.lecture.quotiteRetenue", { pct: String(reading.quotite.recordedPct) })}
                  <span className="block text-sm text-gray-500">
                    {t("console.lecture.quotiteRetenueAide")}
                  </span>
                </p>
              ) : null}
              {reading.quotite.withdrawnWords !== null &&
              reading.quotite.withdrawnWords !== undefined &&
              (reading.quotite.recordedPct === null || reading.quotite.recordedPct === undefined) ? (
                <p className="text-sm text-amber-700" data-testid="lecture-quotite-retiree">
                  {reading.quotite.withdrawnWords}
                </p>
              ) : null}
              <p
                className={
                  reading.quotite.recordedPct === null || reading.quotite.recordedPct === undefined
                    ? ""
                    : "text-gray-500"
                }
                data-testid="lecture-quotite-proposee"
              >
                {reading.quotite.proposedPct === null
                  ? t("console.lecture.quotiteIndisponible")
                  : fill("console.lecture.quotiteProposee", { pct: String(reading.quotite.proposedPct) })}
                {reading.quotite.declaredPct !== null ? (
                  <span className="text-gray-500">
                    {" "}
                    · {fill("console.lecture.quotiteDeclaree", { pct: String(reading.quotite.declaredPct) })}
                  </span>
                ) : null}
              </p>
              {reading.quotite.netSalaryAverageAmount !== null ? (
                <p className="text-sm text-gray-500">
                  {fill("console.lecture.salaireMoyen", {
                    montant: formatMoney(reading.quotite.netSalaryAverageAmount),
                  })}
                  {reading.quotite.capacityAmount !== null
                    ? ` · ${fill("console.lecture.capacite", { montant: formatMoney(reading.quotite.capacityAmount) })}`
                    : ""}
                </p>
              ) : null}
            </div>
          ) : null}

          <div data-testid="lecture-prefiltre">
            <p className="text-sm font-medium text-gray-700">{t("console.lecture.prefiltreTitre")}</p>
            {reading.prefilter === null ? (
              <p className="mt-0.5 text-sm text-gray-500">{t("console.lecture.prefiltreAbsent")}</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {reading.prefilter.retained.map((bank) => (
                  <li
                    key={bank.bankId}
                    className="text-sm text-gray-700"
                    data-testid={`lecture-banque-${bank.bankId}`}
                  >
                    <span className="font-medium">{bank.name}</span> · {t("console.lecture.retenue")} :{" "}
                    {bank.reason}
                  </li>
                ))}
                {reading.prefilter.excluded.map((bank) => (
                  <li
                    key={bank.bankId}
                    className="text-sm text-rose-600"
                    data-testid={`lecture-banque-${bank.bankId}`}
                  >
                    <span className="font-medium">{bank.name}</span> · {t("console.lecture.ecartee")} :{" "}
                    {bank.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {reading.fields.length > 0 ? (
            <p className="text-sm text-gray-500" data-testid="lecture-restant">
              {reading.remaining === 0
                ? t("console.lecture.tousConfirmes")
                : remainingWords(reading.remaining)}
            </p>
          ) : null}

          <div>
            <button
              type="button"
              data-testid="lecture-voir"
              onClick={() => {
                setTrailOpen((now) => !now);
              }}
              className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
              {trailOpen ? t("console.lecture.masquer") : t("console.lecture.voir")}
            </button>
            {trailOpen ? (
              <div className="mt-2" data-testid="lecture-etapes">
                <p className="text-sm font-medium text-gray-700">{t("console.lecture.etapesTitre")}</p>
                <ol className="mt-1 space-y-1">
                  {reading.steps.map((step) => (
                    <li
                      key={step.ordinal}
                      className="text-sm text-gray-600"
                      data-testid={`lecture-etape-${String(step.ordinal)}`}
                    >
                      {stepWords(step)}
                      {step.failure !== null ? (
                        <span className="block text-rose-600">
                          {fill("console.lecture.etapeEchec", { motif: step.failure })}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}

/** The filter's guard as the door says it, for the stage panel: greyed with the count while a field is unacted. */
export function filterGuard(reading: FirstPassReadingView | null | undefined): string | null {
  if (reading === null || reading === undefined) return null;
  if (reading.run.status !== "terminee" || reading.run.outcome !== "reussie") return null;
  return reading.remaining > 0 ? remainingWords(reading.remaining) : null;
}

/** `U-3`, `M1`: the synthesis as the door gives it (WF-CON-04 step 4, `P3-ITL-008`). */
export interface SynthesisView {
  readonly rule: string;
  readonly points: readonly {
    readonly text: string;
    readonly on: string | null;
    readonly tone: "alerte" | "attention" | "ok" | "neutre";
  }[];
  readonly counters: {
    readonly relances: number;
    readonly lastContactOn: string | null;
    readonly preferredChannel: string | null;
    readonly messages: number;
  };
}
